<template>
  <div :class="wrapperClasses">
    <div class="background-wrapper z-10" v-if="backgroundImage || backgroundVideo !== undefined">
      <div v-if="backgroundVideo !== undefined || backgroundImage" class="background-wrapper__overlay z-10"></div>
      <!-- Background Video -->
      <BackgroundVideo
        v-if="backgroundVideo !== undefined && backgroundVideo.poster"
        ref="videoComp"
        :video-field-group="backgroundVideo"
        @canplaythrough="onVideoCanPlayThrough"
        @played="onVideoPlayed"
        @paused="onVideoPaused"
      />
      <!-- Play / Pause -->
      <transition name="fade" v-if="backgroundVideo !== undefined">
        <button
          v-if="videoCanPlayThrough && videoIsPaused !== null"
          id="background-video-playstate-toggle"
          :class="videoPlaystateToggleClasses"
          @click="toggleVideoPlaystate"
          :title="this.htmlDecode(videoPlaystateToggleTitle)"
          :aria-label="this.htmlDecode(videoPlaystateToggleTitle)"
        >
          <transition name="fade">
            <SvgIcon
              class="absolute left-1/2 top-1/2 w-5 h-5 transform-translate-x--1/2-y--1/2"
              :key="videoPlaystateToggleIcon"
              :icon="videoPlaystateToggleIcon"
            />
          </transition>
        </button>
      </transition>
      <img v-if="backgroundImage && !backgroundVideo.mp4 || backgroundImage && !backgroundVideo.poster" class="background-wrapper__img" :src="backgroundImage.sizes.large">
    </div>
    <slot v-if="fullSize" />
    <div
      v-else
      class="relative w-full max-h-full overflow-auto scrolling-touch z-20"
    >
	    <div class="my-20 px-8 lg:px-4">
        <div class="mb-7 last:mb-0">
          <Heading
            v-if="headingText"
            :class="`mb-4 last:mb-0 ${headingColorClass || ''} text-center`"
            larger
            :text="headingText"
          />
          <Subheading
            v-if="subheadingText"
            :class="`mb-4 last:mb-0 ${subheadingColorClass || ''} text-center`"
            :text="subheadingText"
          />
        </div>
		    <slot />
	    </div>
    </div>
  </div>
</template>

<script>
import Heading from '@/components/text/Heading.vue'
import Subheading from '@/components/text/Subheading.vue'
import BackgroundVideo from '@/components/BackgroundVideo.vue'
import SvgIcon from '@/components/SvgIcon.vue'
import { mapGetters } from 'vuex'

export default {
  name: 'Layout',

  props: {
    colorClasses: {
      type: String,
      default: ''
    },
    fullSize: {
      type: Boolean,
      default: false
    },
    headingText: {
      type: String,
      default: ''
    },
    subheadingText: {
      type: String,
      default: ''
    },
    backgroundImage: {
      type: Object,
      default: () => {}
    },
    backgroundVideo: {
      type: Object,
      default: () => {}
    }
  },

  data: () => ({
    videoCanPlayThrough: false,
    videoIsPaused: null
  }),

  components: {
    Heading,
    Subheading,
    BackgroundVideo,
    SvgIcon
  },

  computed: {
    // Store
    ...mapGetters([ 'homePage', 'myStore' ]),
    // Styles
    globalStyles() { return this.myStore(['styles', 'global']) },
    homeStyles() { return this.myStore(['styles', 'home_screen']) ?? {} },
    // Container
    pageStyles() { return this.globalStyles.page ?? {} },
    wrapperClasses() {
      const bgClass = `bg-${this.pageStyles.bg_color || 'off-white'}`
      const textClass = `text-${this.pageStyles.text_color || 'body'}`
      return [
        'LayoutComp',
        'a-cover overflow-hidden',
        'flex justify-center items-center',
        this.colorClasses || `${bgClass} ${textClass}`,
      ]
    },
    // Heading
    headingStyles() { return this.globalStyles.heading ?? {} },
    headingColorClass() { return this.headingStyles.text_color && `text-${this.headingStyles.text_color}` },
    // Subheading
    subheadingStyles() { return this.globalStyles.subheading ?? {} },
    subheadingColorClass() { return this.subheadingStyles.text_color && `text-${this.subheadingStyles.text_color}` },
    // Video Playstate Toggle Btn
    videoPlaystateToggleTitle() {
      const btnLabels = this.myStore(['labels', 'home_screen', 'background', 'video_playstate_toggle']) ?? {}
      const playingLabels = btnLabels.playing ?? {}
      const playingTitle = playingLabels.btn_title || 'Pause video'
      const pausedLabels = btnLabels.paused
      const pausedTitle = pausedLabels.btn_title || 'Resume video'
      return this.videoIsPaused ? pausedTitle : playingTitle
    },
    videoPlaystateToggleClasses() {
      const backgroundStyles = this.homeStyles.background ?? {}
      const btnStyles = backgroundStyles.video_playstate_toggle ?? {}
      const focusStyles = btnStyles.focus ?? {}
      const hoverStyles = btnStyles.hover ?? {}
      return [
        // GTM
        `background-video-playstate-${this.videoIsPaused ? 'paused' : 'playing'}`,
        // Position
        'absolute right-4 top-4',
        // Dimensions
        'w-12 h-12',
        // Z
        'z-20',
        // Display
        'flex-c',
        // Border
        `border-${btnStyles.border_color ?? 'accent-1'}`,
        `border-${btnStyles.border_width ?? 2}`,
        `rounded-${btnStyles.border_radius ?? 2}`,
        `focus:border-${focusStyles.border_color ?? 'black'} lg:hover:border-${hoverStyles.border_color ?? 'accent-1'}`,
        // Background
        `bg-${btnStyles.bg_color ?? 'white'}`,
        `focus:bg-${focusStyles.bg_color ?? 'white'} lg:hover:bg-${hoverStyles.bg_color ?? 'accent-1'}`,
        // Fill
        `fill-${btnStyles.fill_color ?? 'accent-1'}`,
        `focus:fill-${focusStyles.fill_color ?? 'black'} lg:hover:fill-${hoverStyles.fill_color ?? 'white'}`,
        // Outline
        'focus:outline-none',
        // Transitions
        'transition-colors duration-500 ease-in-out',
      ]
    },
    videoPlaystateToggleIcon() { return this.videoIsPaused ? 'play' : 'pause' },
  },

  methods: {
    // Listeners
    onVideoCanPlayThrough() { this.videoCanPlayThrough = true },
    onVideoPlayed() { this.videoIsPaused = false },
    onVideoPaused() { this.videoIsPaused = true },
    // Action
    toggleVideoPlaystate() {
      if (!this.$refs.videoComp) { return }
      this.$refs.videoComp.togglePlaystate()
    }
  }
}

</script>

<style scoped>
  .background-wrapper {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
  }

    .background-wrapper__img {
      height: 100%;
      width: 100%;
      object-fit: cover;
    }

    .background-wrapper__overlay {
      position: absolute;
      height: 100%;
      width: 100%;
      background-color: rgba(0,0,0,0.6);
    }
</style>

